import { getImageBaseUrl } from "../../apis/apiSettings";

import {
  sanitizeImageDetails,
  sanitizeTags,
  appendImagesToFormDataCustomField,
  sanitizeEntryDataResponseBase,
} from "./commonUtils";

/**
 * Sanitizes the response data from a photo entry API.
 *
 * This function sets default values for the fields in case they do not exist in the response.
 * It also sanitizes certain fields that require additional processing.
 *
 * @function
 * @name sanitizePhotoEntryDataResponse
 * @param {Object} entryDataResponse - The original entry data response to be sanitized.
 * @returns {Object} The sanitized photo entry data response. It includes fields
 * like `id`, `created`, `date_modified`, `entry_type`, `physician_owner`, `patient_owner`,
 * `thumbnail`, `summary`, `notes`, `tags`, and `image_[0-9]` with corresponding `image_[0-9]_details`.
 * The `image_[0-9]_details` fields are further sanitized by the `sanitizeImageDetails` function.
 * The `tags` field is sanitized by the `sanitizeTags` function.
 * @example
 * const sanitizedResponse = sanitizePhotoEntryDataResponse({ entryDataResponse: originalResponse });
 */
export const sanitizePhotoEntryDataResponse = ({ entryDataResponse }) => {
  const defaultValues = {
    id: "",
    created: "",
    date_modified: "",
    appointment_date: "",
    entry_type: "",
    physician_owner: "",
    patient_owner: "",
    thumbnail: "",
    summary: "",
    notes: "",
    classification_tags: [],

    image_0: "",
    image_0_details: "{}",
    image_1: "",
    image_1_details: "{}",
    image_2: "",
    image_2_details: "{}",
    image_3: "",
    image_3_details: "{}",
    image_4: "",
    image_4_details: "{}",
    image_5: "",
    image_5_details: "{}",
    image_6: "",
    image_6_details: "{}",
    image_7: "",
    image_7_details: "{}",
    image_8: "",
    image_8_details: "{}",
    image_9: "",
    image_9_details: "{}",
  };

  let sanitizedResponse = sanitizeEntryDataResponseBase({
    entryDataResponse: entryDataResponse,
    defaultValues: defaultValues,
  });

  // Fields which require further sanitization: sanitizeImageDetails, sanitizePairConfiguration,
  // and sanitizeTags functions on the relevant fields.
  for (let i = 0; i < 10; i++) {
    sanitizedResponse[`image_${i}_details`] = sanitizeImageDetails(
      sanitizedResponse[`image_${i}_details`]
    );
  }

  sanitizedResponse.classification_tags = sanitizeTags(
    sanitizedResponse.classification_tags
  );

  return sanitizedResponse;
};

/**
 * Processes the image data from a sanitized photo entry response.
 *
 * This function extracts the image details from a sanitized photo entry response and
 * formats them into two separate lists: one for the images and one for the image details.
 *
 * @function
 * @name processPhotoEntryImages
 * @param {Object} sanitizedEntryDataResponse - The sanitized entry data response that contains the images to be processed.
 * @returns {Object} An object containing two arrays: `apiImages` and `apiImagesDetails`.
 * `apiImages` is an array of objects, each representing an image. Each image object includes
 * the `name`, `size`, `path`, `preview`, `uniqueKey`, and `apiItem` properties.
 * `apiImagesDetails` is an array of objects, each representing the details of an image, including the `caption`.
 * @example
 * const { apiImages, apiImagesDetails } = processPhotoEntryImages({ sanitizedEntryDataResponse: sanitizedResponse });
 */
export const processPhotoEntryImages = ({
  sanitizedEntryDataResponse = {},
}) => {
  const apiImagesDetails = [];
  const apiImages = [];

  for (let i = 0; i < 10; i++) {
    const currentImage = sanitizedEntryDataResponse[`image_${i}`];
    const imageDetails = sanitizedEntryDataResponse[`image_${i}_details`];

    // Only add anything if there is an image
    if (currentImage) {
      apiImages.push({
        name: imageDetails.name,
        size: imageDetails.size,
        path: getImageBaseUrl() + currentImage,
        preview: getImageBaseUrl() + currentImage,
        uniqueKey: currentImage,
        apiItem: true,
      });

      apiImagesDetails.push({ caption: imageDetails.caption });
    }
  }

  // Return the apiImages and apiImagesDetails
  return { apiImages, apiImagesDetails };
};

/**
 * Appends photo files and their corresponding details to a FormData object.
 *
 * This function iterates over an array of files and an array of file details,
 * appending each pair to a FormData object under a specific field name.
 * The field names are generated dynamically based on the index of the current file.
 *
 * @function
 * @name appendPhotosToFormData
 * @param {Object} params - The parameters for the function.
 * @param {FormData} params.formData - The FormData object to append the files and file details to.
 * @param {Array<File>} params.files - An array of File objects representing the images to be appended.
 * @param {Array<Object>} params.filesDetails - An array of objects representing the details of the images to be appended.
 * @returns {void}
 * @example
 * const formData = new FormData();
 * const files = [...]; // Array of File objects
 * const filesDetails = [...]; // Array of file details objects
 * appendPhotosToFormData({ formData, files, filesDetails });
 */
export const appendPhotosToFormData = ({ formData, files, filesDetails }) => {
  // Loop through all files to process each image
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileDetails = filesDetails[i];
    const fileName = `image_${i}`;
    const detailsName = `image_${i}_details`;

    appendImagesToFormDataCustomField({
      formData: formData,
      fileInput: file,
      fileDetails: fileDetails,
      fileName: fileName,
      detailsName: detailsName,
      singleFileArray: false,
    });
  }
};
